// P2b tanda 5 (PARTE A) — pestaña "Guías de tallas" del Catálogo. Lista +
// editor en la misma página con botón de atrás (patrón de Compras), contra el
// proxy /api/catalog/guias* (contrato v0.7.156 del tema Vitrina).
//
// ⚠️ Producción HOY corre Elementor sin el tema (medido 404, 14-sep-2026): el
// backend responde { disponible: false } y esta pestaña se muestra con sus
// acciones DESHABILITADAS y el motivo a la vista — la interfaz existe, dice
// por qué no puede actuar, y se enciende sola cuando Vitrina esté activo.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Plus, Info, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import TablaMedidasEditor from './TablaMedidasEditor';
import ProductImageSelector from './ProductImageSelector';
import { useToast } from './ToastManager';
import { listarGuias, obtenerGuia, crearGuia, actualizarGuia, borrarGuia, listarGuiasCategorias } from '../utils/guiasApi';
import { normalizarTabla, tablaVacia } from '../utils/tablaMedidas';
import { traducirCategorias } from '../utils/traduccionCategorias';
import { updateMediaAlt } from '../utils/productApi';
import { tiposConfigurados, tipoPositivoDeCategoria } from '../utils/tiposProducto';
import { decodeHtml } from '../utils/decodeHtml';
import './GuiasTallasTab.css';

function ImagenSortable({ img, idx, onQuitar, onAlt }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
  const style = { transform: DndCSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={`gt-img${isDragging ? ' gt-img--drag' : ''}`}>
      <img src={img.url} alt={img.alt || ''} {...attributes} {...listeners} />
      <button type="button" className="pp-viconbtn pp-viconbtn--danger gt-img-quitar" title="Quitar imagen"
        aria-label={`Quitar la imagen ${idx + 1} de la guía`} onClick={() => onQuitar(img.id)}>
        <Trash2 size={13} aria-hidden="true" />
      </button>
      <input className="gt-img-alt" value={img.alt || ''} placeholder="Texto alternativo"
        aria-label={`Texto alternativo de la imagen ${idx + 1} de la guía`}
        onChange={(e) => onAlt(img.id, e.target.value)} />
    </div>
  );
}

// Editor de UNA guía (crear o editar) — misma página, botón de atrás.
// `hibrido` (tanda 6): las guías viven en OTRO WordPress — las categorías
// marcadas (ids del catálogo) se TRADUCEN por slug/nombre antes de enviar.
function GuiaEditor({ guia, guias, categories, catalogoConfig, hibrido = false, onVolver, onGuardada }) {
  const toast = useToast();
  const showToast = (toast && toast.showToast) || (() => {});
  const esNueva = !guia;
  const [titulo, setTitulo] = useState(guia?.titulo || '');
  const [tabla, setTabla] = useState(guia?.tabla || tablaVacia());
  const [imagenes, setImagenes] = useState(guia?.imagenes || []); // [{id,url,alt}]
  // Tanda 5b: la "unidad de medida" es la NOTA bajo la tabla (el contrato no
  // tiene campo propio de unidad: viaja en `notas`). Nueva guía nace con el
  // texto sugerido; el campo Notas separado desapareció — un solo escritor.
  const [notas, setNotas] = useState(esNueva ? 'Medidas en centímetros' : (guia?.notas || ''));
  const [terms, setTerms] = useState(() => new Set((guia?.categorias || []).map((c) => c.term_id)));
  const [imgSel, setImgSel] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [error, setError] = useState(null);

  const tipos = useMemo(() => tiposConfigurados(catalogoConfig), [catalogoConfig]);
  // Tanda 6 (parte 0): al grupo PRINCIPAL entran solo las categorías que
  // coinciden POSITIVAMENTE (palabras o ancestro) con un tipo que aplica guía
  // de tallas; las que no coinciden con NINGÚN tipo van al grupo colapsado
  // "Sin tipo definido" (las de un tipo sin tallas quedan fuera, como antes).
  const grupos = useMemo(() => {
    const principal = [];
    const sinTipo = [];
    for (const c of categories) {
      const tipo = tipoPositivoDeCategoria(tipos, c, categories);
      if (tipo && tipo.aplicaTallas) principal.push(c);
      else if (!tipo) sinTipo.push(c);
    }
    const ids = new Set(principal.map((c) => c.id));
    const padres = principal.filter((c) => c.parent === 0 || !ids.has(c.parent));
    return {
      arbol: padres.map((p) => ({ padre: p, hijas: principal.filter((h) => h.parent === p.id) })),
      principal,
      sinTipo,
    };
  }, [categories, tipos]);
  const arbol = grupos.arbol;
  // Avisos de la traducción híbrida (persisten a la vista tras guardar).
  const [sinEquivalente, setSinEquivalente] = useState([]);

  // Tanda 7 (defecto medido por el e2e de staging): el editor abría con la fila
  // de la LISTA, que tras guardar-y-reabrir rápido podía estar VIEJA (onGuardada
  // recarga la lista async; reabrir en ese hueco mostraba la tabla anterior).
  // Al montar en edición se relee la guía POR ID y se pisa el estado con lo
  // fresco; si el GET falla, la vista conserva lo que tenía.
  useEffect(() => {
    if (!guia?.id) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const fresca = await obtenerGuia(guia.id);
        if (cancelado || !fresca || fresca.disponible === false) return;
        setTitulo(fresca.titulo || '');
        setTabla(fresca.tabla || tablaVacia());
        setImagenes(fresca.imagenes || []);
        setNotas(fresca.notas || '');
        setTerms(new Set((fresca.categorias || []).map((c) => c.term_id)));
      } catch { /* la vista conserva lo que tenía */ }
    })();
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guia?.id]);

  // term_id → título de la guía que HOY la usa (para "hoy usa: X" y el aviso de mover).
  const duenaDe = useMemo(() => {
    const m = new Map();
    for (const g of guias || []) {
      if (guia && g.id === guia.id) continue;
      for (const c of g.categorias || []) m.set(c.term_id, g.titulo);
    }
    return m;
  }, [guias, guia]);

  const toggleTerm = (id) => setTerms((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const onImagenes = (imgs) => {
    const list = Array.isArray(imgs) ? imgs.filter(Boolean) : (imgs ? [imgs] : []);
    setImagenes((prev) => {
      let next = [...prev];
      for (const img of list) {
        next = next.filter((x) => x.id !== img.id);
        next.push({ id: img.id, url: img.src || img.url, alt: img.alt || '' });
      }
      return next;
    });
  };
  const quitarImagen = (id) => setImagenes((prev) => prev.filter((i) => i.id !== id));
  const altImagen = (id, alt) => {
    setImagenes((prev) => prev.map((i) => (i.id === id ? { ...i, alt } : i)));
    updateMediaAlt(id, alt).catch(() => {}); // el alt vive en la mediateca (igual que la ficha)
  };
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onImgDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setImagenes((prev) => {
      const from = prev.findIndex((i) => i.id === active.id);
      const to = prev.findIndex((i) => i.id === over.id);
      return from >= 0 && to >= 0 ? arrayMove(prev, from, to) : prev;
    });
  };

  const guardar = async () => {
    setError(null);
    const limpia = normalizarTabla(tabla);
    if (!String(titulo).trim()) { setError('La guía necesita un título.'); return; }
    if (!limpia) { setError('La tabla necesita al menos una columna con nombre y una talla.'); return; }
    setGuardando(true);
    try {
      // Híbrido: NUNCA se envían los ids del catálogo — se traducen contra las
      // categorías del WordPress de guías (slug, luego nombre); lo que no
      // encuentra equivalente se declara y queda fuera sin bloquear el resto.
      let categoriasAEnviar = [...terms];
      let noTraducidas = [];
      if (hibrido && categoriasAEnviar.length) {
        const remotas = await listarGuiasCategorias();
        const trad = traducirCategorias(categoriasAEnviar, categories, remotas.categorias || []);
        categoriasAEnviar = trad.ids;
        noTraducidas = trad.sinEquivalente;
      }
      setSinEquivalente(noTraducidas);
      const body = {
        titulo: String(titulo).trim(),
        tabla: limpia, // OBJETO {columnas, filas}, nunca JSON-en-string (contrato)
        imagenes: imagenes.map((i) => i.id),
        notas,
        categorias: categoriasAEnviar, // reemplaza el conjunto ENTERO (contrato del PUT)
      };
      const guardada = esNueva ? await crearGuia(body) : await actualizarGuia(guia.id, body);
      showToast({ message: `Guía "${guardada.titulo}" guardada.`, variant: 'success' });
      // Con categorías sin equivalente el editor SE QUEDA mostrando el aviso
      // (volver a la lista se lo tragaría); sin avisos, vuelve como siempre.
      if (!noTraducidas.length) onGuardada();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    setGuardando(true);
    try {
      await borrarGuia(guia.id);
      showToast({ message: `Guía "${guia.titulo}" eliminada.`, variant: 'success' });
      setConfirmarBorrar(false);
      onGuardada();
    } catch (e) {
      setConfirmarBorrar(false);
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const nCatsAsignadas = (guia?.categorias || []).length;

  return (
    <div className="gt-editor">
      <button type="button" className="cp-back-btn" onClick={onVolver}>
        <ArrowLeft size={18} /> Guías de tallas
      </button>
      {/* Tanda 5b: encabezado con las acciones alineadas como en la ficha
          (título a la izquierda, Eliminar + Guardar a la derecha). */}
      <div className="gt-editor-head">
        <h3>{esNueva ? 'Nueva guía' : `Editar guía: ${guia.titulo}`}</h3>
        <div className="gt-editor-acciones">
          {!esNueva && <Button onClick={() => setConfirmarBorrar(true)}>Eliminar</Button>}
          <Button variant="primary" onClick={guardar} loading={guardando}>Guardar guía</Button>
        </div>
      </div>
      {error && <div className="pp-saveerror" role="alert">{error}</div>}

      {/* Bloques con el estilo de tarjeta de la ficha de producto. */}
      <div className="gt-card">
        <div className="pp-field">
          <label className="pp-label">Título</label>
          <input className="pp-input gt-titulo" value={titulo} placeholder="Ej.: Bodys"
            aria-label="Título de la guía" onChange={(e) => setTitulo(e.target.value)} />
        </div>
        <div className="pp-subhead">Tabla de medidas</div>
        {/* La tabla editable ES la vista de la tienda; la nota de unidad se
            guarda en `notas` (el contrato no tiene campo propio). */}
        <TablaMedidasEditor tabla={tabla} onChange={setTabla} rotulo="guía"
          nota={notas} onNotaChange={setNotas} />
      </div>

      <div className="gt-card">
        <div className="pp-subhead gt-subhead-primero">Imágenes "cómo medir"</div>
        <p className="pp-help">Una o varias; arrastra para reordenar. Se muestran en el modal de la tienda en este orden.</p>
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onImgDragEnd}>
          <SortableContext items={imagenes.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="gt-imgs">
              {imagenes.map((img, idx) => (
                <ImagenSortable key={img.id} img={img} idx={idx} onQuitar={quitarImagen} onAlt={altImagen} />
              ))}
              <button type="button" className="gt-img-add" onClick={() => setImgSel(true)}>
                <Plus size={16} aria-hidden="true" /> Agregar imagen
              </button>
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <div className="gt-card">
      <div className="pp-subhead gt-subhead-primero">Categorías que usan esta guía</div>
      <p className="pp-help">
        Entran las categorías que coinciden con un tipo de producto que aplica guía de tallas
        (Configuración → Catálogo). Una categoría solo puede usar UNA guía: marcar una ocupada la mueve a esta.
      </p>
      {hibrido && (
        <p className="gt-aviso-hibrido"><Info size={13} aria-hidden="true" /> Las guías apuntan a otro WordPress; las categorías se traducen por nombre.</p>
      )}
      {sinEquivalente.length > 0 && (
        <div className="pp-saveerror" role="alert">
          No existen en el WordPress de guías: {sinEquivalente.join(', ')}. No se enviaron; las demás quedaron guardadas.
        </div>
      )}
      {grupos.principal.length > 0 && (
        <label className="gt-todas">
          <input type="checkbox"
            checked={grupos.principal.every((c) => terms.has(c.id))}
            aria-label="Marcar todas / Desmarcar todas"
            onChange={(e) => {
              const marcar = e.target.checked;
              setTerms((prev) => {
                const next = new Set(prev);
                grupos.principal.forEach((c) => { if (marcar) next.add(c.id); else next.delete(c.id); });
                return next;
              });
            }} />
          Marcar todas / Desmarcar todas
        </label>
      )}
      <div className="pp-cattree gt-cattree">
        {arbol.length === 0 && <p className="pp-muted">No hay categorías con guía de tallas aplicable.</p>}
        {arbol.map(({ padre, hijas }) => (
          <div key={padre.id} className="pp-catgroup">
            {[padre, ...hijas].map((c, i) => {
              const dueña = duenaDe.get(c.id);
              return (
                <label key={c.id} className={`pp-catnode${i > 0 ? ' pp-catchild' : ''}`}>
                  <input type="checkbox" checked={terms.has(c.id)} onChange={() => toggleTerm(c.id)} />
                  {decodeHtml(c.name)}
                  {dueña && (
                    <span className="gt-ocupada">
                      hoy usa: {dueña}{terms.has(c.id) ? ' · se moverá a esta guía al guardar' : ''}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        ))}
        {/* Tanda 6: las que no coinciden con NINGÚN tipo — colapsadas, aparte,
            solo desmarcables (marcar una sin tipo sería clasificarla a ciegas). */}
        {grupos.sinTipo.length > 0 && (
          <details className="gt-sintipo">
            <summary>Sin tipo definido ({grupos.sinTipo.length})</summary>
            <p className="pp-help">
              Asigna el tipo por palabras de categoría en Configuración → Catálogo o corrige la categoría.
            </p>
            {grupos.sinTipo.map((c) => (
              <label key={c.id} className="pp-catnode">
                <input type="checkbox" checked={terms.has(c.id)} disabled={!terms.has(c.id)}
                  onChange={() => toggleTerm(c.id)} />
                {decodeHtml(c.name)}
              </label>
            ))}
          </details>
        )}
      </div>
      </div>

      <ProductImageSelector open={imgSel} onClose={() => setImgSel(false)} onSelect={onImagenes}
        nombre={titulo || 'guia-de-tallas'} variante="" />

      <ConfirmDialog
        open={confirmarBorrar}
        title="Eliminar guía"
        confirmLabel="Eliminar guía"
        loading={guardando}
        onConfirm={eliminar}
        onCancel={() => setConfirmarBorrar(false)}
      >
        <p>
          Vas a eliminar la guía "{guia?.titulo}".
          {nCatsAsignadas > 0
            ? ` ${nCatsAsignadas} ${nCatsAsignadas === 1 ? 'categoría quedará' : 'categorías quedarán'} sin guía de tallas.`
            : ' No tiene categorías asignadas.'}
          {' '}Esta acción no se puede deshacer.
        </p>
      </ConfirmDialog>
    </div>
  );
}

export default function GuiasTallasTab({ categories = [], catalogoConfig = null }) {
  const [estado, setEstado] = useState({ cargando: true, disponible: false, motivo: '', guias: [] });
  const [buscar, setBuscar] = useState('');
  const [editor, setEditor] = useState(null); // null | { guia: null | objeto }

  const cargar = useCallback(async () => {
    try {
      const r = await listarGuias();
      setEstado({ cargando: false, disponible: !!r.disponible, hibrido: !!r.hibrido, motivo: r.motivo || '', guias: r.guias || [] });
    } catch (e) {
      setEstado({ cargando: false, disponible: false, hibrido: false, motivo: e.message, guias: [] });
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return estado.guias;
    return estado.guias.filter((g) => String(g.titulo || '').toLowerCase().includes(q));
  }, [estado.guias, buscar]);

  if (editor) {
    return (
      <GuiaEditor
        guia={editor.guia}
        guias={estado.guias}
        categories={categories}
        catalogoConfig={catalogoConfig}
        hibrido={estado.hibrido}
        onVolver={() => setEditor(null)}
        onGuardada={() => { setEditor(null); cargar(); }}
      />
    );
  }

  return (
    <div className="gt-lista">
      {!estado.cargando && !estado.disponible && (
        <div className="gt-no-disponible" role="status">
          <Info size={15} aria-hidden="true" />
          {estado.motivo || 'Disponible cuando el tema Vitrina esté activo en la tienda'}
        </div>
      )}
      {/* Tanda 6: aviso PERMANENTE del modo híbrido en la lista. */}
      {!estado.cargando && estado.disponible && estado.hibrido && (
        <div className="gt-no-disponible" role="status">
          <Info size={15} aria-hidden="true" />
          Las guías apuntan a otro WordPress; las categorías se traducen por nombre.
        </div>
      )}
      <div className="gt-toolbar">
        <input className="pp-input gt-buscar" value={buscar} placeholder="Buscar guía por título…"
          aria-label="Buscar guía" onChange={(e) => setBuscar(e.target.value)} />
        <Button variant="primary" disabled={!estado.disponible}
          title={estado.disponible ? undefined : (estado.motivo || 'Disponible cuando el tema Vitrina esté activo en la tienda')}
          onClick={() => setEditor({ guia: null })}>
          <Plus size={15} aria-hidden="true" /> Nueva guía
        </Button>
      </div>
      {estado.cargando ? (
        <p className="pp-muted">Cargando guías…</p>
      ) : filtradas.length === 0 ? (
        <p className="pp-muted">
          {estado.disponible
            ? (buscar ? 'Ninguna guía coincide con la búsqueda.' : 'Todavía no hay guías de tallas.')
            : 'Las guías se administran desde aquí cuando el tema esté activo.'}
        </p>
      ) : (
        // Tanda 5b: filas con la altura y el espaciado de las tablas de líneas
        // de Compras (grilla, encabezado en versalitas, borde por fila).
        <div className="gt-ltabla">
          <div className="gt-lgrid gt-lhead">
            <span>Título</span><span>Categorías</span><span>Imágenes</span><span>Fecha</span>
          </div>
          {filtradas.map((g) => (
            <div className="gt-lgrid gt-lfila" key={g.id}>
              <button type="button" className="gt-ltitulo" onClick={() => setEditor({ guia: g })}
                aria-label={`Editar la guía ${g.titulo}`}>{g.titulo}</button>
              {/* el conteo dice CUÁLES en el tooltip */}
              <span title={(g.categorias || []).map((c) => c.nombre).join(', ') || 'Sin categorías asignadas'}>
                {(g.categorias || []).length}
              </span>
              <span>{(g.imagenes || []).length}</span>
              {/* El contrato v0.7.156 no expone fecha de modificación: se
                  muestra si algún día viaja, sin inventarla. */}
              <span>{g.fecha ? new Date(g.fecha).toLocaleDateString('es-CO') : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
